/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { useApplicationState } from '../../hooks/common/use-application-state'
import { useBaseUrl } from '../../hooks/common/use-base-url'
import { cypressId } from '../../utils/cypress-attribute'
import { Logger } from '../../utils/logger'
import { UiIcon } from '../common/icons/ui-icon'
import { useUiNotifications } from '../notifications/ui-notification-boundary'
import { getNoteLabel } from './build-note-tree'
import { NoteType, type NoteTreeEntryInterface } from '@hedgedoc/commons'
import React, { useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Dropdown } from 'react-bootstrap'
import {
  BoxArrowUpRight as IconNewTab,
  Easel as IconSlideshow,
  Eye as IconReadOnly,
  Link45deg as IconLink,
  Pencil as IconEdit,
  Trash as IconTrash
} from 'react-bootstrap-icons'
import { Trans, useTranslation } from 'react-i18next'
import { buildNoteUrl } from '../../utils/note-url'

const logger = new Logger('NoteTreeContextMenu')

export interface ContextMenuPosition {
  x: number
  y: number
}

export interface NoteTreeContextMenuProps {
  note: NoteTreeEntryInterface
  position: ContextMenuPosition
  onClose: () => void
  onDelete: (note: NoteTreeEntryInterface) => void
  onEdit: (note: NoteTreeEntryInterface) => void
}

/**
 * Renders the context menu for a note in the navigation tree at the given position on the screen.
 * The menu closes when an entry is chosen, when clicking outside of it or when pressing escape.
 * It is rendered into the document body, because the sticky sidebar creates its own stacking context
 * and the editor next to it would otherwise be painted over the menu.
 */
export const NoteTreeContextMenu: React.FC<NoteTreeContextMenuProps> = ({
  note,
  position,
  onClose,
  onDelete,
  onEdit
}) => {
  useTranslation()
  const baseUrl = useBaseUrl()
  const username = useApplicationState((state) => state.user?.username)
  const { dispatchUiNotification, showErrorNotificationBuilder } = useUiNotifications()
  const isOwner = note.owner !== null && note.owner === username

  const onToggle = useCallback(
    (nextShow: boolean) => {
      if (!nextShow) {
        onClose()
      }
    },
    [onClose]
  )

  const onCopyLink = useCallback(() => {
    const url = `${baseUrl}${buildNoteUrl(note.primaryAlias).slice(1)}`
    const copyPromise =
      typeof navigator.clipboard === 'undefined'
        ? Promise.reject(new Error('Clipboard API is not available'))
        : navigator.clipboard.writeText(url)
    copyPromise
      .then(() =>
        dispatchUiNotification('copyOverlay.success', 'noteTree.contextMenu.linkCopied', {
          durationInSecond: 3,
          contentI18nOptions: { title: getNoteLabel(note) }
        })
      )
      .catch((error: Error) => {
        logger.error('Copy failed', error)
        showErrorNotificationBuilder('copyOverlay.error')(error)
      })
      .finally(onClose)
  }, [baseUrl, dispatchUiNotification, note, onClose, showErrorNotificationBuilder])

  const onClickEdit = useCallback(() => {
    onClose()
    onEdit(note)
  }, [note, onClose, onEdit])

  const onClickDelete = useCallback(() => {
    onClose()
    onDelete(note)
  }, [note, onClose, onDelete])

  const anchorStyle = useMemo(
    () => ({ position: 'fixed' as const, left: position.x, top: position.y, zIndex: 1050 }),
    [position.x, position.y]
  )

  return createPortal(
    <div style={anchorStyle} {...cypressId('note-tree-context-menu')}>
      <Dropdown show={true} onToggle={onToggle}>
        <Dropdown.Toggle as={'span'} className={'d-block'} aria-hidden={true} />
        <Dropdown.Menu>
          <Dropdown.Item
            href={buildNoteUrl(note.primaryAlias)}
            target={'_blank'}
            rel={'noopener noreferrer'}
            onClick={onClose}>
            <UiIcon icon={IconNewTab} className={'me-2'} />
            <Trans i18nKey={'noteTree.contextMenu.openInNewTab'} />
          </Dropdown.Item>
          <Dropdown.Item
            href={buildNoteUrl(note.primaryAlias, 's')}
            target={'_blank'}
            rel={'noopener noreferrer'}
            onClick={onClose}>
            <UiIcon icon={IconReadOnly} className={'me-2'} />
            <Trans i18nKey={'noteTree.contextMenu.openReadOnly'} />
          </Dropdown.Item>
          {note.type === NoteType.SLIDE && (
            <Dropdown.Item
              href={buildNoteUrl(note.primaryAlias, 'p')}
              target={'_blank'}
              rel={'noopener noreferrer'}
              onClick={onClose}>
              <UiIcon icon={IconSlideshow} className={'me-2'} />
              <Trans i18nKey={'noteTree.contextMenu.openSlideshow'} />
            </Dropdown.Item>
          )}
          <Dropdown.Item as={'button'} onClick={onClickEdit} {...cypressId('note-tree-context-menu-edit')}>
            <UiIcon icon={IconEdit} className={'me-2'} />
            <Trans i18nKey={'noteTree.contextMenu.edit'} />
          </Dropdown.Item>
          <Dropdown.Item as={'button'} onClick={onCopyLink}>
            <UiIcon icon={IconLink} className={'me-2'} />
            <Trans i18nKey={'noteTree.contextMenu.copyLink'} />
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item as={'button'} onClick={onClickDelete} disabled={!isOwner}>
            <UiIcon icon={IconTrash} className={'me-2'} />
            <Trans i18nKey={'editor.modal.deleteNote.title'} />
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>,
    document.body
  )
}
